import React, { useState, useCallback } from "react";

import { Link } from "react-router-dom";

import { Button } from "./ui/button.tsx";
import { Dialog, DialogContent } from "./ui/dialog.tsx";
import { Card, CardContent, CardFooter } from "./ui/card.tsx";
import { Badge } from "./ui/badge.tsx";
import { Label } from "./ui/label.tsx";
import { Switch } from "./ui/switch.tsx";

import { CheckIcon, CircleArrowRightIcon, ZapIcon, X } from "lucide-react";

import { cn } from "../lib/utils.js";

import { loadStripe } from "@stripe/stripe-js";
import {
    EmbeddedCheckoutProvider,
    EmbeddedCheckout,
} from "@stripe/react-stripe-js";

import clsx from "clsx";

const API_VERSION: any = process.env.REACT_APP_API_VERSION;
let BACKEND_URL: string = process.env.REACT_APP_SERVICE_URL + API_VERSION;
const STRIPE_PUBLIC_KEY: string = process.env.REACT_APP_STRIPE_PUBLIC_KEY || "";

import { useAuth } from "@clerk/clerk-react";
import { useFormEditorContext } from "../Contexts/FormEditorContext.jsx";

const template = "custom_jwt_template";

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

type FixedPrice = string;

interface VariablePrice {
    monthly: string;
    yearly: string;
}

interface Plan {
    name: string;
    type?: "pro" | "business" | "enterprise";
    price: FixedPrice | VariablePrice;
    description: string;
    capacity: string[];
    features: string[];
    isStarter: boolean;
    isRecommended: boolean;
    buttonText: string;
    buttonLink: string;
    buttonStyle: "outline" | "default";
    isCustom?: boolean;
}

const plans: Plan[] = [
    {
        name: "Free Forever",
        price: "$0",
        description: "Best for personal use",
        capacity: ["Up to 5 users, 1 admin", "1 workspace"],
        features: [
            "Unlimited Forms",
            "Unlimited Submissions",
            "Partial Submissions (view only) & Tracking",
            "AI Form Creation",
            "File Uploads",
            "Basic Analytics & Insights",
            "Custom 'Thank You' Page",
            "Embed Images & Videos",
            "Custom themes",
            "Google Sheets Integration",
            "Calendly integration",
            "Workflow Builder (Logics)",
        ],
        isStarter: true,
        isRecommended: false,
        buttonText: "Current Plan",
        buttonLink: "#",
        buttonStyle: "outline",
    },
    {
        name: "Pro",
        price: { monthly: "$29", yearly: "$23" },
        type: "pro",
        description: "Best for small teams",
        capacity: ["Up to 500 users, 10 admins", "Unlimited workspaces"],
        features: [
            "Everything in Free, plus:",
            "Remove Buildform Branding",
            "Custom Branding",
            "Custom Domains for Forms",
            "Redirect to URL",
            "Admin Email Notification",
            "Multiple workspaces",
            "Webhooks",
        ],
        isStarter: false,
        isRecommended: true,
        buttonText: "Subscribe",
        buttonLink: "#",
        buttonStyle: "default",
    },
    {
        name: "Business",
        price: { monthly: "$149", yearly: "$99" },
        type: "business",
        description: "Best for personal use",
        capacity: ["Up to 500 users, 10 admins", "Unlimited workspaces"],
        features: [
            "Everything in Pro, plus:",
            "Advanced Workflow Builder (Logics) & rules",
            "Advanced Analytics",
            "Priority Support",
            "Large File Uploads (100MB+)",
            "Build logics with AI",
            /*    "API Access",
            "Data Retention & Security Controls",
            "Payments (0% + Payment gateway charges)",
            "DeepOptimize (Advanced)",
            "1000 AI credits",
            "5 team seats",
            "Multi-language forms",
            "Save & Resume / Respondent Accounts",
            "CRM integrations",
            "Video Answers", */
        ],
        isStarter: false,
        isRecommended: false,
        buttonText: "Subscribe",
        buttonLink: "#",
        buttonStyle: "outline",
    },
    {
        name: "Enterprise",
        price: "Custom",
        type: "enterprise",
        description: "Best for large organizations",
        capacity: ["Up to 500 users, 10 admins", "Unlimited workspaces"],
        features: [
            "Everything in Business, plus:",
            "Enterprise API",
            "MSA & HIPAA Available",
            "Single Sign-On (SSO)",
            "Live Onboarding Training",
            "Customer Success Manager",
            "Dedicated Lead Gen Expert Support",
            "Data retention & audit logs",
            /*    "Unlimited AI credits",
            "Unlimited users",
            "24/7 support",
            "Custom AI model tuning (e.g., industry-specific DeepOptimize suggestions)",
            "Guaranteed SLA / 99.9% uptime",
            "Role-based access control", */
        ],
        isStarter: false,
        isRecommended: false,
        buttonText: "Contact Sales",
        buttonLink: "#",
        buttonStyle: "outline",
        isCustom: true,
    },
];

type PricingModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

const isVariablePrice = (
    price: FixedPrice | VariablePrice,
): price is VariablePrice => {
    return (price as VariablePrice).monthly !== undefined;
};

export function PricingModal({ isOpen, onClose }: PricingModalProps) {
    const [billingFrequency, setBillingFrequency] = useState<
        "monthly" | "yearly"
    >("yearly");
    const [planType, setPlanType] = useState<"pro" | "business">("pro");

    const [showEmbedded, setShowEmbedded] = useState<boolean>(false);
    const { setOpenCalendly } = useFormEditorContext();

    const { getToken } = useAuth();
    const handleCheckout = useCallback(
        async (planType) => {
            const token = await getToken({ template });

            try {
                const response = await fetch(
                    `${BACKEND_URL}/payment/create-checkout-session?plan_type=${planType}&plan_subscription_type=${billingFrequency}&success_url=${window.location.href}?payment_return=true&cancel_url=${window.location.href}`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    },
                );

                const data = await response.json();
                const url = data.url;

                // Redirect to Stripe Checkout
                window.location.href = url;
            } catch (error) {
                console.error("Error creating checkout session:", error);
                // Handle error (e.g., show error message to user)
            }
        },
        [planType, billingFrequency, getToken],
    );

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent
                hideClose
                className={clsx(
                    showEmbedded ? "max-w-[1040px]" : "max-w-[1382px]",
                    "gap-0 top-[50%]  z-[9999] px-0 py-0 w-full  rounded-[16px] border-none outline-none ",
                )}
                onInteractOutside={(e) => {
                    e.preventDefault();
                    onClose();
                    if (showEmbedded) {
                        setShowEmbedded(false);
                    }
                }}
            >
                {showEmbedded ? (
                    <PaymentSection
                        planType={planType}
                        planSubscriptionType={billingFrequency}
                    />
                ) : (
                    <>
                        <section
                            aria-labelledby="pricing-title"
                            className="flex flex-col relative justify-center w-full mx-auto h-[170px] text-center animate-slide-up-fade "
                            style={{
                                animationDuration: "600ms",
                                animationFillMode: "backwards",
                                backgroundImage: `url('/plansBackground.png')`,
                                position: "relative",
                                backgroundSize: "cover",
                            }}
                        >
                            <Button
                                variant={"outline"}
                                className="absolute border shadow-md bg-[#FFFFFF] hover:bg-[#FFFFFF] border-[#DCDFE4] p-0 size-9 right-4 top-4"
                                onClick={onClose}
                            >
                                <X size={16} className=" text-[#44546F]" />
                            </Button>
                            <h1 className="mt-2 inline-block text-primary bg-clip-text font-bold tracking-tighter text-[36px]">
                                Our plans scale with you
                            </h1>
                            <p className="max-w-2xl mx-auto mt-2 text-sm text-center text-gray-700">
                                Plans that empower you and your team to ship
                                without friction. <br /> Our flexible pricing
                                models ensure that efficiency doesn&rsquo;t come
                                at the cost of your budget.
                            </p>
                        </section>

                        <section
                            id="pricing-overview"
                            className="px-2 py-2 mx-auto mt-6 animate-slide-up-fade w-fit"
                            aria-labelledby="pricing-overview"
                            style={{
                                animationDuration: "600ms",
                                animationDelay: "200ms",
                                animationFillMode: "backwards",
                            }}
                        >
                            <div className="flex relative gap-2 justify-center items-center mx-auto mt-2.5 w-fit">
                                <Label
                                    htmlFor="switch"
                                    className="text-base font-medium text-gray-400 sm:text-sm"
                                >
                                    Monthly
                                </Label>
                                <Switch
                                    id="switch"
                                    checked={billingFrequency === "yearly"}
                                    className="data-[state=checked]:bg-[#CF9FFF]"
                                    onCheckedChange={() =>
                                        setBillingFrequency(
                                            billingFrequency === "monthly"
                                                ? "yearly"
                                                : "monthly",
                                        )
                                    }
                                />
                                <Label
                                    htmlFor="switch"
                                    className="text-base font-medium sm:text-sm"
                                >
                                    Yearly
                                </Label>
                                <div className="absolute -top-8 -right-16">
                                    <Badge className="text-xs text-[#8B22C3] bg-[#FAF0FF] px-2 hover:bg-[#FAF0FF]">
                                        20% OFF
                                    </Badge>
                                    <img
                                        src="/spiral-arrow.svg"
                                        alt="Spiral Arrow"
                                        className="w-[24px] ml-3"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 px-6 mt-6 gap-x-4 gap-y-6 lg:grid-cols-4">
                                {plans.map((plan, planIdx) => (
                                    <Card
                                        key={planIdx}
                                        className={cn(
                                            "border-gray-200 max-w-[367px] p-6 flex flex-col justify-between rounded-[12px] shadow-none",
                                            plan.isRecommended &&
                                                "border-[#CF9FFF]",
                                        )}
                                    >
                                        <CardContent className="p-0">
                                            <div className="">
                                                <div className="flex flex-row items-center justify-between">
                                                    <h2 className="font-semibold text-gray-900 text-md">
                                                        {plan.name}
                                                    </h2>
                                                    {plan.isRecommended && (
                                                        <Badge className="text-xs text-[#8B22C3] bg-[#FAF0FF] hover:bg-[#FAF0FF]">
                                                            <ZapIcon
                                                                fill="#8B22C3"
                                                                className="w-3 h-3 text-[#8B22C3] mr-[2px]"
                                                            />
                                                            Most Popular
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="mt-1 flex items-center gap-x-[6px]">
                                                    <span className="text-[38px] font-bold tabular-nums text-gray-900">
                                                        {isVariablePrice(
                                                            plan.price,
                                                        )
                                                            ? billingFrequency ===
                                                              "monthly"
                                                                ? plan.price
                                                                      .monthly
                                                                : plan.price
                                                                      .yearly
                                                            : plan.price}
                                                    </span>
                                                    {plan.name !==
                                                        "Enterprise" && (
                                                        <div className="text-xs text-gray-700">
                                                            per user/monthly
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex flex-col justify-between mt-3">
                                                    <p className="text-sm leading-6 text-gray-600">
                                                        {plan.description}
                                                    </p>
                                                </div>
                                                <ul
                                                    role="list"
                                                    className="mt-3 text-sm h-[207px] [&::-webkit-scrollbar]:w-2
  [&::-webkit-scrollbar-track]:rounded-full
  [&::-webkit-scrollbar-track]:bg-gray-100
  [&::-webkit-scrollbar-thumb]:rounded-full
  [&::-webkit-scrollbar-thumb]:bg-gray-300
   overflow-y-auto text-gray-700 gap-y-[14px]"
                                                >
                                                    {plan.features.map(
                                                        (feature) => (
                                                            <li
                                                                key={feature}
                                                                className="flex items-center gap-x-[6px] py-2"
                                                            >
                                                                <div className="bg-[#5DC38E] w-4 h-4 flex items-center justify-center rounded-full">
                                                                    <CheckIcon
                                                                        className="text-white size-3 shrink-0"
                                                                        aria-hidden="true"
                                                                    />
                                                                </div>
                                                                <span>
                                                                    {feature}
                                                                </span>
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            </div>
                                        </CardContent>
                                        <CardFooter className="w-full p-0 mt-5">
                                            {plan?.isStarter ? (
                                                <Button
                                                    variant={plan.buttonStyle}
                                                    asChild
                                                    className="w-full h-12 group"
                                                >
                                                    <Link to={plan.buttonLink}>
                                                        {plan.buttonText}
                                                        <CircleArrowRightIcon className="w-5 h-5 ml-2" />
                                                    </Link>
                                                </Button>
                                            ) : plan?.isCustom ? (
                                                <Button
                                                    variant={plan.buttonStyle}
                                                    asChild
                                                    className="w-full h-12 group"
                                                    onClick={() => {
                                                        setOpenCalendly(true);
                                                        onClose();
                                                    }}
                                                >
                                                    <Link to={plan.buttonLink}>
                                                        {plan.buttonText}
                                                        <CircleArrowRightIcon className="w-5 h-5 ml-2" />
                                                    </Link>
                                                </Button>
                                            ) : (
                                                <Button
                                                    asChild
                                                    className="w-full h-12 group"
                                                    variant={plan.buttonStyle}
                                                    onClick={() => {
                                                        handleCheckout(
                                                            plan.type,
                                                        );
                                                    }}
                                                >
                                                    <Link to={plan.buttonLink}>
                                                        {plan.buttonText}
                                                        <CircleArrowRightIcon className="w-5 h-5 ml-2" />
                                                    </Link>
                                                </Button>
                                            )}
                                        </CardFooter>
                                    </Card>
                                ))}
                            </div>
                        </section>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

const PaymentSection = ({
    planType,
    planSubscriptionType,
}: {
    planType: string;
    planSubscriptionType: string;
}) => {
    const { getToken } = useAuth();
    const fetchClientSecret = useCallback(async () => {
        // Retrieve the token
        const token = await getToken({ template });

        // Create a Checkout Session
        return fetch(
            `${BACKEND_URL}/payment/create-checkout-session?plan_type=${planType}&plan_subscription_type=${planSubscriptionType}&return_url=${window.location.href}`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            },
        )
            .then((res) => res.json())
            .then((data) => data.client_secret);
    }, [getToken]);

    const options = { fetchClientSecret };

    return (
        <div id="checkout">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
                <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
        </div>
    );
};
